import { formatDate, parse } from 'date-fns';
import findLastIndex from 'lodash/findLastIndex';
import keyBy from 'lodash/keyBy';
import Cookies from 'js-cookie';
import { useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { PlayerSession, RegistrationStepConfig } from '../../components/Registration/types';
import { FormField } from '../../components/shared/Form';
import { countryCodeOptions } from '../../components/shared/TelephoneInput/countryCodes';
import { useRegisterUserAutoLoginMutation } from '../../libs/graphql/baseAppAPI/mutations/__generated__/register-user-auto-login.mutation.generated';
import { capitalizeFirstLetter } from '../../utils/form';
import { PhoneNumber } from '../../utils/phone-number';
import { updateFormDataByFieldConfig } from './useRegistrationStepper.helper';
import { BaseRegistrationStepConfig, FlattenedProfileData } from './useRegistrationStepper.model';
import { trackEvent } from '../../tracking/tracking';
import { RegistrationSuccessEvent } from '../../tracking/types';
import { setXtremepushId, xtremepushEvent } from '../../utils/xtremepush';
import { btag, locale, xTenantId } from '../../consts';
import { storeOTAT } from '../../utils/auto-login';
import { useContentPageGetBySlugQuery } from '../../libs/graphql/betzoneDirectusAPI/queries/__generated__/content-page-get-by-slug.query.generated';
import { Endpoint } from '../../models/api.model';
import { seonService } from '../../components/Account/seon/seon-service';
import { useUserContext } from '../../providers/UserProvider';
import { getRedirectUri } from '../../components/Authentification/helper';
import { useSignIn } from '../auth';

const equalsStepId =
  (stepId: string) =>
  (step: BaseRegistrationStepConfig): boolean =>
    step.id === stepId;

/**
 * Get a key value map of player form field configurations.
 * @param config
 */
export type RegistrationFieldMap = Record<string, FormField<Partial<FlattenedProfileData>>>;

export type UseRegistrationStepperOptions = {
  /**
   * Initial form state for registration data
   */
  defaultFormState: FlattenedProfileData;
  /**
   * List of registration steps to be used.
   */
  registrationSteps: RegistrationStepConfig[];
  /**
   * Form field configurations
   */
  formFields: RegistrationFieldMap;
  /**
   * Optionally update request values.
   */
  showIntroduction?: boolean;
  mapRequestValues?: (formState: FlattenedProfileData) => FlattenedProfileData;
};

export type UseRegistrationStepperReturnValue = {
  /**
   * Index of currently selected step.
   */
  selectedStepIndex: number;
  /**
   * Index of previously selected step if there is one.
   */
  previouslySelectedStepIndex?: number;
  /**
   * Currently selected step configuration
   */
  selectedStep: BaseRegistrationStepConfig;
  /**
   * Step index to redirect to if user tries to manually access a disabled step route.
   */
  redirectStepIndex: number;
  /**
   * Current form state.
   * The stepper logic will merge state updates over top of default form state
   * when steps are completed or there is a request to merge partially completed data.
   * This may happen when the user navigates to a previous.
   */
  formState: FlattenedProfileData;
  /**
   * State to keep track of status of each step.
   */
  status: Record<number, { completed: boolean; stepNumber: number; enabled: boolean }>;
  /**
   * True if currently making request to register player.
   */
  isSubmitting: boolean;
  /**
   * Call this when the user has completed current step to trigger form state update and move to next step.
   * @param stepIndex
   * @param stepFormData
   */
  completeStep(stepIndex: number, stepFormData: Partial<FlattenedProfileData>): void;
  /**
   * Should be called when the user invalidates previously completed step (by deleting some data etc)
   * @param stepIndex
   */
  invalidateStep(stepIndex: number): void;
  /**
   * Go back to to previous step.  Should be called when user clicks back.
   */
  previousStep(): void;
  /**
   * Should be called to merge in partial form data to save it before navigation occurs.
   * @param partialRegistrationData
   */
  mergePartialData(partialRegistrationData: Partial<FlattenedProfileData>): void;
  /**
   * Request to set the selected step by step id.  Will only proceed if user is allowed to switch to step.
   * @param stepId
   */
  setSelectedStep(stepId: string): void;
  registrationSuccessResponse: PlayerSession;
  registrationErrors: string;
};

/**
 * Registration stepper logic hook.
 *
 * Used for registration and made available for potential use in custom registration implementations.
 * Also keeps the registration UI cleaner.
 *
 * Some of the logic could potentially be extracted into a more general stepper hook
 * if other components which depend on stepper logic are needed.
 */
export function useRegistrationStepper({
  defaultFormState,
  registrationSteps,
  formFields,
}: UseRegistrationStepperOptions): UseRegistrationStepperReturnValue {
  const [formState, setFormState] = useState<FlattenedProfileData>(defaultFormState);
  const auth = useAuth();
  const signIn = useSignIn();

  const formSubmitted = useRef(false);
  const [formLoading, setFormLoading] = useState<boolean>(false);
  const { theme } = useUserContext();

  const [registrationSuccessResponse, setRegistrationSuccessResponse] = useState<PlayerSession>({
    access_token: 'token',
    expires_in: 3600,
    token_type: 'type',
    refresh_token: 'refresh',
  });
  const [registrationErrors, setRegistrationErrors] = useState<string>('');
  const [selectedStepIndex, setSelectedStepIndex] = useState(0);

  const [previouslySelectedStepIndex, setPreviouslySelectedStepIndex] = useState<number>();
  const [status, setStatus] = useState(
    keyBy(
      registrationSteps.map((_, stepNumber) => {
        return {
          stepNumber,
          completed: false,
          enabled: stepNumber === 0,
        };
      }),
      (s) => s.stepNumber
    )
  );

  const { data: termsData } = useContentPageGetBySlugQuery({
    variables: {
      slug: 'terms-and-conditions',
      language: locale,
    },
    context: { endpoint: Endpoint.betzoneDirectusAPI },
  });

  const { data: privacyData } = useContentPageGetBySlugQuery({
    variables: {
      slug: 'privacy-policy',
      language: locale,
    },
    context: { endpoint: Endpoint.betzoneDirectusAPI },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onRegistrationFailed = (error?: any): void => {
    const nextStepId = 'error';
    const errorStepIndex = registrationSteps.findIndex(equalsStepId(nextStepId));

    setRegistrationErrors('reg error');
    disableOtherSteps(nextStepId);
    nextStep(errorStepIndex);
    setFormLoading(false);
    console.error('Registration failed: ', error);
  };

  const [registerUserAutoLoginMutation, { loading }] = useRegisterUserAutoLoginMutation({
    onCompleted: async (data, clientOptions) => {
      if (!data.registerUserAutoLogin?.data) {
        onRegistrationFailed('Registration failed');

        return;
      }

      const registrationData = data.registerUserAutoLogin.registrationData;

      setRegistrationSuccessResponse({
        access_token: 'token',
        expires_in: 3600,
        token_type: 'type',
        refresh_token: 'refresh',
      });

      const model = clientOptions?.variables?.model;

      await seonService(
        {
          givenName: model.givenName,
          familyName: model.familyName,
          birthDate: model.birthDate,
          email: model.email,
          mobileNumber: {
            prefix: model.mobilePrefix,
            number: model.mobileNumber,
          },
          address: {
            addressCountry: model.addrCountryCode,
            addressLocality: model.addrLocality,
            addressRegion: model.addrRegion,
            postalCode: model.addrPostalCode,
            streetAddress: model.addrStreetAddress,
          },
        },
        registrationData?.userProfileId as string
      ).catch(console.error);
      trackEvent<RegistrationSuccessEvent>({
        event: 'regconfirm',
        playerId: registrationData?.userProfileId as string,
        playerEmail: model.email,
        playerPhone: model.mobilePrefix + model.mobileNumber,
      });
      setXtremepushId(registrationData?.userProfileId as string);
      xtremepushEvent('reg_success');
      setFormLoading(false);

      // Handle auto-login if enabled
      if (registrationData?.isAutoLoginEnabled && registrationData?.oneTimeAuthToken && auth) {
        storeOTAT(registrationData.oneTimeAuthToken);

        // Redirect to Keycloak with auto-login parameters
        signIn({
          redirectUri: getRedirectUri(`/${theme}`),
          extraQueryParams: {
            profileID: registrationData.userProfileId || '',
            locale,
            autoLogin: 'true',
          },
        });

        return; // Don't continue with normal flow, redirect to login
      }

      const successStepIndex = registrationSteps.findIndex(equalsStepId('success'));

      nextStep(successStepIndex);
    },
    onError: (error) => {
      onRegistrationFailed(error);
    },
  });

  const selectedStep = registrationSteps[selectedStepIndex];

  const nextStep = (nextStepIndex: number): void => {
    /* istanbul ignore if */
    if (nextStepIndex < 0 || nextStepIndex >= registrationSteps.length) return;

    setPreviouslySelectedStepIndex(selectedStepIndex);
    setSelectedStepIndex(nextStepIndex);
  };

  const disableOtherSteps = (excludedStepId: string): void => {
    setStatus(
      keyBy(
        registrationSteps.map((step, stepNumber) => ({
          stepNumber,
          completed: false,
          enabled: step.id === excludedStepId,
        })),
        (s) => s.stepNumber
      )
    );
  };

  const previousStep = (): void => {
    if (selectedStepIndex === 0 || !selectedStep.backEnabled) return;

    setSelectedStepIndex(selectedStepIndex - 1);
  };

  const setSelectedStep = (stepId: string, firstStep?: boolean): void => {
    const isIntroduction = stepId === 'introduction';

    firstStep && nextStep(0);

    const stepIndex = registrationSteps.findIndex(equalsStepId(stepId));
    const step = registrationSteps[stepIndex];

    if (!step || (!status[stepIndex].completed && !isIntroduction)) return;

    if (isIntroduction && selectedStepIndex) {
      return previousStep();
    }

    nextStep(stepIndex);
  };

  const invalidateStep = (stepIndex: number): void => {
    const currentStepStatus = status[stepIndex];

    if (currentStepStatus.completed) {
      const step = registrationSteps[stepIndex];

      setSelectedStep(step.id);
      setStatus({
        ...status,
        [stepIndex]: { ...currentStepStatus, completed: false },
      });
    }
  };

  const completeStep = async (stepIndex: number, data: Partial<FlattenedProfileData>): Promise<void> => {
    if (formSubmitted.current) return;

    const step = registrationSteps[stepIndex];

    console.log('completeStep called with data:', data);

    const nextFormState = {
      ...formState,
      ...updateFormDataByFieldConfig(data, formFields),
    };

    console.log('Updated form state:', nextFormState);

    setFormState(nextFormState);
    setStatus({
      ...status,
      [stepIndex]: {
        ...status[stepIndex],
        completed: true,
      },
      [stepIndex + 1]: {
        ...status[stepIndex + 1],
        enabled: true,
      },
    });

    if (step.submissionStep) {
      if (loading) return;
      formSubmitted.current = true;

      try {
        const {
          toggleAllMarketing,
          optInEmail,
          optInSms,
          optInTelephone,
          optInEmailCasino,
          optInSmsCasino,
          optInTelephoneCasino,
          optInEmailBingo,
          optInSmsBingo,
          optInTelephoneBingo,
          promoCode,
          acceptTerms,
          givenName,
          familyName,
          ...others
        } = nextFormState;

        console.log('Marketing preferences before submission:', {
          optInEmailCasino,
          optInSmsCasino,
          optInTelephoneCasino,
          optInEmailBingo,
          optInSmsBingo,
          optInTelephoneBingo,
        });

        const phone = new PhoneNumber(nextFormState.mobileNumber, countryCodeOptions);
        const updatedPhoneNumber = phone.getPurePhoneNumber();
        const countryCode = phone.getCountryCodeValue();
        const parsedDate = parse(nextFormState.birthDate, 'dd/MM/yyyy', new Date());
        const dateOfBirth = formatDate(parsedDate, 'yyyy-MM-dd');
        const utmCookie = Cookies.get(btag);

        // Get UTM parameters from cookies
        const utmSource = Cookies.get('utm_source');
        const utmMedium = Cookies.get('utm_medium');
        const utmCampaign = Cookies.get('utm_campaign');
        const utmContent = Cookies.get('utm_content');
        const utmTerm = Cookies.get('utm_term');

        const productMarketingOptIn = {
          casino: {
            callOptIn: nextFormState.optInTelephoneCasino || false,
            emailOptIn: nextFormState.optInEmailCasino || false,
            postalMailOptIn: false,
            pushNotificationOptIn: false,
            smsOptIn: nextFormState.optInSmsCasino || false,
          },
          bingo: {
            callOptIn: nextFormState.optInTelephoneBingo || false,
            emailOptIn: nextFormState.optInEmailBingo || false,
            postalMailOptIn: false,
            pushNotificationOptIn: false,
            smsOptIn: nextFormState.optInSmsBingo || false,
          },
        };

        console.log('productMarketingOptIn object being submitted:', productMarketingOptIn);

        disableOtherSteps('success');
        // Create a new object by filtering out acceptMarketingPreferences from 'others'
        const filteredOthers = Object.fromEntries(
          Object.entries(others).filter(([key]) => key !== 'acceptMarketingPreferences')
        );

        const registrationData = {
          ...filteredOthers,
          mobileNumber: updatedPhoneNumber,
          mobilePrefix: countryCode,
          gender: 'none',
          givenName: capitalizeFirstLetter(givenName),
          familyName: capitalizeFirstLetter(familyName),
          birthDate: dateOfBirth,
          termsAndConditionsVersion: termsData?.documents?.[0]?.revisions?.[0]?.document_revisions_id?.version ?? '0.1',
          privacyPolicyVersion: privacyData?.documents?.[0]?.revisions?.[0]?.document_revisions_id?.version ?? '0.1',
          ...(utmCookie && { utm_payload: utmCookie }),
          ...(utmSource && { utm_source: utmSource }),
          ...(utmMedium && { utm_medium: utmMedium }),
          ...(utmContent && { utm_content: utmContent }),
          ...(utmTerm && { utm_term: utmTerm }),
          ...(utmCampaign && { utm_campaign: utmCampaign }),
          productMarketingOptIn,
        };

        setFormLoading(true);
        await registerUserAutoLoginMutation({
          variables: {
            model: registrationData,
            xTenantId: xTenantId,
          },
        });
      } catch (error) {
        setFormLoading(false);
        onRegistrationFailed(error);
      }
    } else {
      nextStep(stepIndex + 1);
    }
  };

  const lastCompletedIndex = findLastIndex(registrationSteps, (_, i) => status[i] && status[i].completed);
  const redirectStepIndex = lastCompletedIndex === -1 ? 0 : lastCompletedIndex;

  const mergePartialData = (regData: Partial<FlattenedProfileData>): void => {
    setFormState({ ...formState, ...regData });
  };

  return {
    selectedStep,
    redirectStepIndex,
    selectedStepIndex,
    formState,
    status,
    previouslySelectedStepIndex,
    registrationSuccessResponse,
    registrationErrors,
    isSubmitting: loading || formLoading,
    completeStep,
    invalidateStep,
    previousStep,
    mergePartialData,
    setSelectedStep,
  };
}
